import { Link, useLocation } from "react-router-dom";
import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

const navItems = [
  { label: "商家專區", icon: "ri-dashboard-3-line", path: "/admin/dashboard" },
  { label: "手動下單", icon: "ri-shopping-cart-2-line", path: "/admin/manual-order" },
  { label: "現場訂單", icon: "ri-file-list-3-line", path: "/admin/orders" },
  { label: "資源商品管理", icon: "ri-shopping-bag-3-line", path: "/admin/products" },
  { label: "會員資訊中心", icon: "ri-team-line", path: "/admin/members" },
  { label: "累計訂單管理", icon: "ri-bar-chart-grouped-line", path: "/admin/total-orders" },
  { label: "儲值審核", icon: "ri-wallet-3-line", path: "/admin/recharge" },
  { label: "提現審核", icon: "ri-bank-card-line", path: "/admin/withdrawals" },
  { label: "實名審核", icon: "ri-id-card-line", path: "/admin/kyc" },
  { label: "系統設定", icon: "ri-settings-3-line", path: "/admin/settings" },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const sidebarWidth = collapsed ? "w-20" : "w-64";

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 h-screen bg-[#0f172a] text-white flex flex-col transition-all duration-300 ${sidebarWidth} ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo area */}
        <div className="h-16 flex items-center px-5 border-b border-white/10 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-store-3-line text-white text-sm"></i>
          </div>
          {!collapsed && (
            <div className="ml-3 overflow-hidden">
              <p className="text-sm font-bold truncate">吉時國際</p>
              <p className="text-[10px] text-slate-400 truncate">管理後台</p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto hidden md:flex w-7 h-7 items-center justify-center rounded-md hover:bg-white/10 cursor-pointer transition-colors"
          >
            <i className={`ri-arrow-left-s-line text-xs ${collapsed ? "rotate-180" : ""}`}></i>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                  isActive
                    ? "bg-amber-500 text-white font-semibold shadow-sm"
                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom logout */}
        <div className="p-3 border-t border-white/10 flex-shrink-0">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer w-full ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-logout-box-r-line text-base"></i>
            </div>
            {!collapsed && <span>登出系統</span>}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
              <i className="ri-menu-line text-gray-600"></i>
            </button>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-gray-800">
                {navItems.find((n) => n.path === location.pathname)?.label || "管理後台"}
              </p>
              <p className="text-[10px] text-gray-400">
                {new Date().toLocaleDateString("zh-TW", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  weekday: "long",
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors relative">
              <i className="ri-notification-3-line text-base"></i>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 border-2 border-white"></span>
            </button>
            <div className="flex items-center gap-2 pl-2 md:pl-4 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0">
                <i className="ri-user-line text-sm"></i>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                  {user?.name || "管理員"}
                </p>
                <p className="text-[10px] text-gray-400 truncate max-w-[120px]">
                  {user?.email || ""}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}